/**
 * rackFlyout.logic.ts — Pure functional domain logic for RackFlyoutDrawer and DcViewer.
 *
 * Implements:
 * - Mini-elevation unit grouping and descending slot generation (U_total down to U1).
 * - Multi-U equipment and reservation collision-safe rendering slices.
 * - Power, weight, and space metrics aggregation with safe defensive calculations.
 * - Canonical shareable link formatting.
 */

import type { SpaceMetrics, RackReservation, EquipmentSlotItem } from './rackReservation.logic.ts';
import { calculateSpaceMetrics, getReservationUnitRange } from './rackReservation.logic.ts';

export const EQUIPMENT_TYPE_COLORS: Record<string, string> = {
  server: '#00d2ff',
  switch: '#9d4edd',
  storage: '#ff9e00',
  firewall: '#ff007f',
  router: '#39ff14',
  pdu: '#ff003c',
  ups: '#ffb703',
  patch_panel: '#00f5d4',
  default: '#60a5fa',
};

export interface ElevationSlot {
  uStart: number;
  uEnd: number;
  heightU: number;
  type: 'equipment' | 'reservation' | 'empty';
  label: string;
  equipment?: any;
  reservation?: RackReservation;
  color?: string;
}

export interface RackFlyoutMetrics {
  space: SpaceMetrics;
  totalPowerW: number;
  maxPowerW: number;
  powerLoadPct: number;
  totalWeightKg: number;
  maxWeightKg: number;
  weightLoadPct: number;
  zeroUEquipment: any[];
}

/**
 * Computes mini-elevation slots descending from totalU down to 1.
 * Groups contiguous multi-U devices and reservations into cohesive visual blocks.
 */
export function computeMiniElevationSlots(
  totalU: number,
  equipmentList: EquipmentSlotItem[] = [],
  reservations: RackReservation[] = []
): ElevationSlot[] {
  const maxU = Math.max(1, Math.floor(Number.isFinite(totalU) ? totalU : 42));

  // Map each unit to equipment or reservation
  const unitEquipmentMap = new Map<number, EquipmentSlotItem>();
  for (const eq of equipmentList) {
    if (eq.is_zero_u) continue;
    const pos = Number(eq.u_position);
    if (!Number.isFinite(pos)) continue;
    const start = Math.floor(pos);
    const height = Math.max(1, Math.floor(Number(eq.u_height) || 1));
    const end = start + height - 1;
    for (let u = start; u <= end; u++) {
      if (u >= 1 && u <= maxU) {
        unitEquipmentMap.set(u, eq);
      }
    }
  }

  const unitResMap = new Map<number, RackReservation>();
  for (const res of reservations) {
    const { start, end } = getReservationUnitRange(res);
    for (let u = start; u <= end; u++) {
      if (u >= 1 && u <= maxU && !unitEquipmentMap.has(u)) {
        unitResMap.set(u, res);
      }
    }
  }

  const slots: ElevationSlot[] = [];
  let u = maxU;

  while (u >= 1) {
    if (unitEquipmentMap.has(u)) {
      const eq = unitEquipmentMap.get(u)!;
      const start = Math.max(1, Math.floor(eq.u_position));
      const height = Math.max(1, Math.floor(eq.u_height || 1));
      const end = Math.min(maxU, start + height - 1);
      const spanHeight = end - start + 1;
      const eqType = (eq as any).equipment_type?.toLowerCase() || 'server';
      const color = EQUIPMENT_TYPE_COLORS[eqType] || EQUIPMENT_TYPE_COLORS.default;

      slots.push({
        uStart: start,
        uEnd: end,
        heightU: spanHeight,
        type: 'equipment',
        label: eq.name || `Прибор (${spanHeight}U)`,
        equipment: eq,
        color,
      });

      u = start - 1;
    } else if (unitResMap.has(u)) {
      const res = unitResMap.get(u)!;
      const { start, end } = getReservationUnitRange(res);
      const clampStart = Math.max(1, start);
      const clampEnd = Math.min(maxU, end);
      const spanHeight = clampEnd - clampStart + 1;

      slots.push({
        uStart: clampStart,
        uEnd: clampEnd,
        heightU: spanHeight,
        type: 'reservation',
        label: res.description || res.tenant || 'Резерв',
        reservation: res,
        color: '#f59e0b',
      });

      u = clampStart - 1;
    } else {
      slots.push({
        uStart: u,
        uEnd: u,
        heightU: 1,
        type: 'empty',
        label: `U${u}`,
      });
      u -= 1;
    }
  }

  return slots;
}

/**
 * Aggregates space, power, and weight utilization metrics.
 */
export function computeRackUtilizationMetrics(
  totalU: number,
  equipmentList: any[] = [],
  reservations: RackReservation[] = [],
  maxPowerW: number = 5000,
  maxWeightKg: number = 1000
): RackFlyoutMetrics {
  const space = calculateSpaceMetrics(totalU, equipmentList, reservations);

  let totalPowerW = 0;
  let totalWeightKg = 0;
  const zeroUEquipment: any[] = [];

  for (const eq of equipmentList) {
    if (eq.is_zero_u) {
      zeroUEquipment.push(eq);
    }
    const p = Number(eq.power_w);
    if (Number.isFinite(p) && p > 0) {
      totalPowerW += p;
    }
    const w = Number(eq.weight_kg);
    if (Number.isFinite(w) && w > 0) {
      totalWeightKg += w;
    }
  }

  const numMaxP = Number(maxPowerW);
  const safeMaxP = Number.isFinite(numMaxP) && numMaxP > 0 ? numMaxP : 5000;
  const numMaxW = Number(maxWeightKg);
  const safeMaxW = Number.isFinite(numMaxW) && numMaxW > 0 ? numMaxW : 1000;

  const powerLoadPct = Math.round((totalPowerW / safeMaxP) * 100);
  const weightLoadPct = Math.round((totalWeightKg / safeMaxW) * 100);

  return {
    space,
    totalPowerW,
    maxPowerW: safeMaxP,
    powerLoadPct,
    totalWeightKg,
    maxWeightKg: safeMaxW,
    weightLoadPct,
    zeroUEquipment,
  };
}

/**
 * Generates canonical passport URL for rack sharing.
 */
export function formatShareableRackUrl(origin: string, dcCode: string, rackId: number | string): string {
  const cleanOrigin = (origin || '').replace(/\/+$/, '');
  const cleanDc = encodeURIComponent(String(dcCode || '').trim());
  const cleanId = encodeURIComponent(String(rackId ?? '').trim());
  return `${cleanOrigin}/${cleanDc}/racks/${cleanId}`;
}
